import Link from "next/link";

import { ParticipationExplainer } from "../ParticipationExplainer";

import { CityDocumentsSection } from "./CityDocumentsSection";
import { CloseControls } from "./CloseControls";
import { CommunitiesSection } from "./CommunitiesSection";
import { CompactParticipationStrip } from "./CompactParticipationStrip";
import { DescriptionSection } from "./DescriptionSection";
import { DetailDossier } from "./DetailDossier";
import { DetailMasthead } from "./DetailMasthead";
import { IdentifiersSection } from "./IdentifiersSection";
import { LandUseChange } from "./LandUseChange";
import { WardsSection } from "./WardsSection";

import type { Hearing } from "@/lib/public-hearings/types";

type Props = {
  hearing: Hearing;
  /**
   * `compact` is set when the detail renders inside the list-page's
   * right pane (replacing the map). Hides the back-link eyebrow and
   * swaps the full <ParticipationExplainer> for a slim CTA strip. The
   * list page already renders the canonical explainer at the bottom,
   * so the inline-pane trusts the user has read it and surfaces only
   * the per-hearing actions.
   */
  compact?: boolean;
  /**
   * When set, applied as `id` on the title <h1> so external
   * `aria-labelledby` references can target it.
   */
  titleId?: string;
  /**
   * When provided, renders a "Close" affordance at the top of the
   * detail block that navigates back to the list (with current
   * filters preserved). Used by the inline list-detail pane; left
   * unset on the standalone /[slug] page where browser-back is the
   * affordance.
   */
  closeHref?: string;
  /**
   * Optional client-side close callback. When provided, CloseControls
   * calls this instead of navigating to closeHref, avoiding a server
   * round-trip when the detail is rendered inline from client state.
   */
  onClose?: () => void;
};

// Hearing dossier — a single hearing pulled out of the day-block and
// laid bare. Composition follows the editorial register: folio
// masthead, glanceable 3-stat dossier strip, promoted land-use change
// panel, paper-card prose, labeled affected/identifier/document grids,
// then the action block. Each child sits in the `.ph-detail-reveal`
// wrapper so the staggered fade-in walks down the brief on mount.
export function HearingDetail({
  hearing: h,
  compact = false,
  titleId,
  closeHref,
  onClose,
}: Props) {
  return (
    <article className="relative ph-detail-reveal">
      {closeHref && <CloseControls closeHref={closeHref} onClose={onClose} />}

      {!compact && (
        <p className="cs-mono" style={{ marginBottom: 14 }}>
          <Link
            href="/council/public-hearings"
            className="hover:text-ink"
            style={{ color: "var(--ink-3)" }}
          >
            ← upcoming hearings
          </Link>
        </p>
      )}

      <DetailMasthead hearing={h} titleId={titleId} compact={compact} />

      <DetailDossier hearing={h} />

      <LandUseChange hearing={h} />

      <DescriptionSection dmapUrls={h.dmapUrls} />

      <WardsSection wardCouncillors={h.wardCouncillors} dmapUrls={h.dmapUrls} />

      <CommunitiesSection communities={h.communities} />

      <IdentifiersSection
        locCodes={h.locCodes}
        bylawNumbers={h.bylawNumbers}
      />

      <CityDocumentsSection hearing={h} />

      {compact ? (
        <CompactParticipationStrip
          publicSubmissionFormUrl={h.publicSubmissionFormUrl}
          submissionDeadlineUtc={h.submissionDeadlineUtc}
        />
      ) : (
        <ParticipationExplainer
          submissionDeadlineUtc={h.submissionDeadlineUtc}
          publicSubmissionFormUrl={h.publicSubmissionFormUrl}
        />
      )}
    </article>
  );
}
